// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Button, Group, Loader, Pagination, Stack, Table, Text } from '@mantine/core';
import { IconCalendarRepeat, IconMessage, IconVideo } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ProviderAppointmentDetails } from '../../hooks/useProviderAppointmentDetails';
import classes from '../../pages/schedule/SchedulePage.module.css';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import { TableAnchor } from '../layout/TableAnchor';

const BRAND = '#230415';

const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  booked: 'Scheduled',
  fulfilled: 'Completed',
  cancelled: 'Cancelled',
  noshow: 'No Show',
};

const ITEMS_PER_PAGE = 10;

interface TodaysAppointmentsTableProps {
  appointments: ProviderAppointmentDetails[];
  loading: boolean;
  searchQuery: string;
  showProvider?: boolean;
  onReschedule?: (appointment: ProviderAppointmentDetails) => void;
  onJoin?: (appointment: ProviderAppointmentDetails) => void;
}

/**
 * Table displaying today's active appointments for the provider.
 * Shows time, patient, appointment type, documents, and action buttons.
 */
export function TodaysAppointmentsTable({
  appointments,
  loading,
  searchQuery,
  showProvider = false,
  onReschedule,
  onJoin,
}: TodaysAppointmentsTableProps): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  // Filter appointments by search query
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) {
      return appointments;
    }
    const q = searchQuery.toLowerCase();
    return appointments.filter(
      (d) =>
        d.patientName.toLowerCase().includes(q) ||
        d.providerName.toLowerCase().includes(q) ||
        d.appointmentType.toLowerCase().includes(q)
    );
  }, [appointments, searchQuery]);

  // Reset page when search changes
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const currentPage = Math.min(page, Math.max(totalPages, 1));
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (loading) {
    return (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  if (appointments.length === 0) {
    return (
      <Text c="dimmed" py="md">
        No appointments scheduled for today.
      </Text>
    );
  }

  return (
    <>
      <Table className={classes.table}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Time</Table.Th>
            <Table.Th>Patient</Table.Th>
            {showProvider && <Table.Th>Provider</Table.Th>}
            <Table.Th>Appointment Type</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginated.map((details) => (
            <Table.Tr key={details.appointment.id}>
              <Table.Td>
                <AppointmentTimeWithTimezone
                  start={details.appointment.start}
                  end={details.appointment.end}
                  textProps={{ size: 'sm' }}
                />
              </Table.Td>
              <Table.Td>
                <TableAnchor
                  size="sm"
                  onClick={() => {
                    if (details.patientId) {
                      navigate(`/Patient/${details.patientId}`)?.catch(console.error);
                    }
                  }}
                >
                  {details.patientName}
                </TableAnchor>
              </Table.Td>
              {showProvider && <Table.Td>{details.providerName}</Table.Td>}
              <Table.Td>{details.appointmentType}</Table.Td>
              <Table.Td>
                <Badge color={BRAND} variant="light" size="sm">
                  {APPOINTMENT_STATUS_LABELS[details.appointment.status as string] ?? details.appointment.status}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="subtle"
                    className={classes.action}
                    leftSection={<IconCalendarRepeat size={14} />}
                    onClick={() => onReschedule?.(details)}
                  >
                    Reschedule
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    className={classes.action}
                    leftSection={<IconMessage size={14} />}
                    onClick={() => {
                      if (details.patientId) {
                        navigate(`/Patient/${details.patientId}/Communication`)?.catch(console.error);
                      }
                    }}
                  >
                    Message Patient
                  </Button>
                  <Button
                    size="xs"
                    leftSection={<IconVideo size={14} />}
                    className={classes.joinAction}
                    onClick={() => onJoin?.(details)}
                  >
                    Join
                  </Button>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {totalPages > 1 && (
        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            {filtered.length} of {appointments.length}
          </Text>
          <Pagination value={currentPage} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}
    </>
  );
}
